import { type FC, type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Copy,
  Download as DownloadIcon,
  Images,
  Info,
  Loader2,
  Logs,
  MessagesSquare,
  NotebookPen,
  RectangleEllipsis,
  Route,
  Settings2,
  Share,
  Tags as TagsIcon,
  TextCursorInput,
  UserPen,
  Users as UsersIcon,
  Workflow,
} from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth0 } from '@auth0/auth0-react';
import { z } from 'zod';
import {
  type ContributorMetadataDefinition,
  MetadataPanelFormProvider,
} from '@/components/inspector/metadata-accordion/metadata-panel-form';
import { DestinationAccordion } from '@/components/inspector/destination-accordion/destination-accordion';
import { InspectorTabList, type InspectorTabListItem } from '@/components/inspector/inspector-tab-list';
import { isVersionsTabEnabledForShare } from '@/components/inspector/is-versions-tab-enabled-for-share';
import { MetadataAccordion } from '@/components/inspector/metadata-accordion/metadata-accordion';
import { MetadataSaveBar } from '@/components/inspector/metadata-accordion/metadata-save-bar';
import { FileInformationAccordion } from '@/components/inspector/file-information-accordion';
import { DestinationFields } from '@/components/inspector/entity-fields/destination-fields';
import { isSubmissionsTabEnabled } from '@/components/inspector/is-submissions-tab-enabled';
import { InvitationFields } from '@/components/inspector/entity-fields/invitation-fields';
import { usePublicAsset, usePublicMultipleAssets } from '@/hooks/shares/useNodeShares';
import { MetadataFields } from '@/components/inspector/entity-fields/metadata-fields';
import { WorkflowFields } from '@/components/inspector/entity-fields/workflow-fields';
import { isDownloadTabEnabled } from '@/components/inspector/is-download-tab-enabled';
import { type RowEntity, useTableStore } from '@/hooks/data/datatable/useTableStore';
import { VariantFields } from '@/components/inspector/entity-fields/variant-fields';
import { InformationAccordion } from '@/components/inspector/information-accordion';
import { SubmissionsAccordion } from '@/components/inspector/submissions-accordion';
import { useUserSettingsStore } from '@/hooks/data/settings/useUserSettingsStore';
import { ServiceDeskSection } from '@/components/inspector/service-desk-section';
import { CameraInformation } from '@/components/inspector/camera-information';
import { UserFields } from '@/components/inspector/entity-fields/user-fields';
import { VariantsAccordion } from '@/components/inspector/variants-accordion';
import { VersionsAccordion } from '@/components/inspector/versions-accordion';
import { WorkflowAccordion } from '@/components/inspector/workflow-accordion';
import { TagFields } from '@/components/inspector/entity-fields/tag-fields';
import { PreviewAccordion } from '@/components/inspector/preview-accordion';
import { UploadsAccordion } from '@/components/inspector/uploads-accordion';
import { SharesAccordion } from '@/components/inspector/shares-accordion';
import { useMultipleAsset } from '@/hooks/data/assets/useMultipleAssets';
import { NotesAccordion } from '@/components/inspector/notes-accordion';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { useFeatureStore } from '@/hooks/data/feature/useFeatureStore';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { CommentsPanel } from '@/components/inspector/comments-panel';
import { NodeLocations } from '@/components/inspector/node-locations';
import { TagsAccordion } from '@/components/inspector/tags-accordion';
import { TrashedBanner } from '@/components/inspector/trashed-banner';
import { AuditLogAccordion } from '@/components/inspector/audit-log';
import { useAssetRating } from '@/hooks/data/assets/useAssetRating';
import { useIsSearching } from '@/hooks/data/search/useIsSearching';
import { useSearchStore } from '@/hooks/data/search/useSearchStore';
import { useAssetChannel } from '@/hooks/websocket/useAssetChannel';
import { useSelection } from '@/hooks/data/selection/useSelection';
import { useShareConfig } from '@/context/ShareConfigContext';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { Label, LabelVariants } from '@/components/ui/label';
import { useAccount } from '@/hooks/data/account/useAccount';
import type { Asset, BaseAssetVersion } from '@/types/asset';
import { cn, getUploadCsvKey, isAsset } from '@/lib/utils';
import { useFolder } from '@/hooks/data/folder/useFolder';
import { useCapabilities } from '@/hooks/useCapabilities';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { useAlbum } from '@/hooks/data/albums/useAlbum';
import { useAsset } from '@/hooks/data/assets/useAsset';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { useTreeContext } from '@/context/TreeContext';
import { Accordion } from '@/components/ui/accordion';
import { DAMNodeType, NodeType } from '@/types/tree';
import { Rating } from '@/components/rating';
import { EntityType } from '@/types/entity';
import { TaskState } from '@/types/uploads';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';

export enum InspectorTabs {
  // Inspector
  Information = 'inspector-info',
  Metadata = 'inspector-metadata',
  Notes = 'inspector-notes',
  Comments = 'inspector-comments',
  Versions = 'inspector-versions',
  Download = 'inspector-download',
  Destinations = 'inspector-destinations',
  Sharing = 'inspector-sharing',
  Submissions = 'inspector-submissions',
  AuditLog = 'inspector-audit-log',
  Upload = 'inspector-upload',

  // Settings pages
  // Configuration
  Workflows = 'manage-workflows',
  Tags = 'manage-tags',
  Invitations = 'manage-invitations',
  ManageMetadata = 'manage-metadata',
  Variants = 'manage-variants',
  ManageDestinations = 'manage-destinations',

  // Management
  Users = 'manage-users',
}

export type InspectorConfigurationItem = InspectorTabListItem<InspectorTabs> & {
  requiresDownloadCapability?: boolean;
};

export const getCapabilityFilteredInspectorConfiguration = (
  configuration: Array<InspectorConfigurationItem>,
  canDownloadAssets: boolean,
) => configuration.filter(({ requiresDownloadCapability }) => !requiresDownloadCapability || canDownloadAssets);

export const Inspector: FC<{
  data: Array<Asset>;
  pagination?: { offset: number; limit: number; count: number };
  singleAssetShare?: boolean;
  singleAssetView?: boolean;
  isUpload?: boolean;
  /** Content for the Upload tab; falls back to the tenant's folder/albums/settings accordion. */
  uploadTabContent?: ReactNode;
  /** Metadata field definitions for the Metadata tab, sourced outside the tenant metadata-list
   * endpoint — see MetadataPanelFormProvider's contributorMetadataDefinitions prop for why. */
  contributorMetadataDefinitions?: Array<ContributorMetadataDefinition>;
}> = ({
  data,
  pagination,
  singleAssetShare,
  singleAssetView,
  isUpload,
  uploadTabContent,
  contributorMetadataDefinitions,
}) => {
  const tValidation = useTranslations('validation');
  const tLabels = useTranslations('labels');
  const tCommon = useTranslations('common');
  const tInspector = useTranslations('inspector');
  const tEmptyStates = useTranslations('emptyStates');
  const tSelection = useTranslations('selection');
  const tSubmissions = useTranslations('submissions');
  const shareConfig = useShareConfig();
  const { isManagePage, isTrashPage, isHomePage, isSharesPage, isPublicSharePage, isSubmitPage } = useCurrentPage();
  const {
    canViewPrivateAsset,
    canViewPrivateAlbum,
    canViewPrivateTabs,
    canViewComments,
    canDownloadAssets,
    canManageSubmissionLinks,
  } = useCapabilities();

  const destinationsFeature = useFeatureStore((state) => state.destinations);
  const shareCollaborationFeature = useFeatureStore((state) => state.share_collaboration);
  const assetSubmissionFeature = useFeatureStore((state) => state.asset_submission);

  const defaultTab = isSharesPage ? InspectorTabs.Sharing : InspectorTabs.Information;

  const [currentAccordionItems, setCurrentAccordionItems] = useState<Array<string>>(['metadata', 'file-information']);
  const [currentInspectorTab, setCurrentInspectorTab] = useState<InspectorTabs>(defaultTab);

  const { guest, profileLoading } = useAccount();
  const showInspector = useUserSettingsStore((s) => s.showInspector);
  const { isAuthenticated, isLoading: authIsLoading } = useAuth0();
  const { currentSelectedType: typeFromTree, includeSubFolderAssets } = useTreeContext();
  const { selectedNode } = useTreeStore();
  const { selectedEntity, selectedEntityType } = useTableStore();
  const { selectedAssets: browserSelectedAssetsIds } = useGallerySelection();
  // A selected child folder/album (browse pages only) — the panel describes it in place
  // of the active tree node, falling back to the active node once nothing is selected.
  // `describedNodeId` is the shared resolution of that fallback: the name/description writes in
  // InformationAccordion resolve through the same value, so the panel cannot describe one entity
  // while editing another. The two queries below gate on `describedNodeType` from that same
  // resolution, rather than each re-deriving the fallback inline.
  const { describedNodeId, describedNodeType } = useSelection();

  /*
   * Whether the described node could be an album / a folder — the gate the `useAlbum` and `useFolder`
   * queries need below.
   *
   * `undefined` means nothing is described yet (no selected child, no active tree node) and both read
   * as permitted: each query also gates on its own `currentSelectedType` clause, and refusing here
   * would stop the panel loading anything at all on a fresh page load.
   */
  const describedNodeCouldBeAlbum = describedNodeType === undefined || describedNodeType === DAMNodeType.Album;
  const describedNodeCouldBeFolder = describedNodeType === undefined || describedNodeType === DAMNodeType.Folder;
  const { getActiveUploadSession, setManualMetadata } = useUploadStore();
  const { uploadSessionId, session } = getActiveUploadSession();
  const {
    erroredAssets,
    filesData,
    uploadState,
    selectedAssets: uploadSelectedAssetsIds,
    assetsUploadProgress,
    manualMetadata,
    csvData,
    currentSelectedType: uploadCurrentSelectedType,
  } = session;

  const formSchema = z.object({
    ...(!isUpload && {
      name: z
        .string()
        .min(1, { message: tValidation('fieldRequired', { field: tValidation('fields.assetName') }) })
        .max(255, { message: tValidation('fieldMaxLength', { field: tValidation('fields.assetName'), max: 255 }) }),
    }),
    description: z.any(),
  });
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  });

  const { query } = useRouter();
  const requestedInspectorTabFromQuery = useMemo(() => {
    const queryTab = typeof query.inspectorTab === 'string' ? query.inspectorTab : null;

    if (!queryTab) {
      return null;
    }

    const availableTabs = Object.values(InspectorTabs) as Array<string>;
    if (!availableTabs.includes(queryTab)) {
      return null;
    }

    return queryTab as InspectorTabs;
  }, [query.inspectorTab]);
  const { isAllSelected } = useBrowserStore();
  const { setRating } = useAssetRating();
  const { setRatingOverride } = useSearchStore();
  const search = useIsSearching();

  const selectedItems = useMemo(() => {
    if (isUpload) {
      return uploadSelectedAssetsIds;
    }
    if (singleAssetView && typeFromTree === NodeType.Assets) {
      return [{ id: data[0]?.id, name: data[0]?.name, type: DAMNodeType.Asset }];
    }
    return browserSelectedAssetsIds;
  }, [uploadSelectedAssetsIds, browserSelectedAssetsIds, isUpload, singleAssetView, typeFromTree, data]);

  const currentSelectedType = isPublicSharePage
    ? selectedItems.length > 0 || singleAssetShare
      ? NodeType.Assets
      : undefined
    : isUpload
      ? uploadCurrentSelectedType
      : typeFromTree;

  const multipleAssetSelected = isUpload
    ? uploadSelectedAssetsIds.length > 1
    : selectedItems.length > 1 || isAllSelected;

  const trashedSelectedAssets = useMemo(
    () => browserSelectedAssetsIds.some((asset) => asset.trashed || Boolean(asset.trashedAt)),
    [browserSelectedAssetsIds],
  );

  const onlyAssetsSelected =
    multipleAssetSelected && !isUpload
      ? browserSelectedAssetsIds.every((asset) => isAsset(asset.type)) || isAllSelected
      : true;

  const temporaryAssets = useMemo(() => {
    const selectedIds = selectedItems.map((asset) => asset.id);
    return filesData.filter((file) => selectedIds.includes(file.id));
  }, [filesData, selectedItems]);

  const {
    asset: privateAsset,
    isAssetLoading,
    isAssetFetching,
    isPending: isPrivateAssetPending,
  } = useAsset(selectedItems[0]?.id, {
    refetchOnWindowFocus: false,
    enabled:
      showInspector &&
      selectedItems.length === 1 &&
      currentSelectedType === NodeType.Assets &&
      (!isUpload ? isAsset(browserSelectedAssetsIds[0]?.type) : true) &&
      canViewPrivateAsset &&
      (isUpload ? assetsUploadProgress[selectedItems[0]?.id]?.uploaded === true : true),
  });

  // Subscribe to asset channel so `.asset.updated` events merge new
  // variants/dimensions/metadata into the inspector's asset cache.
  useAssetChannel(selectedItems[0]?.id, {
    enabled:
      showInspector &&
      selectedItems.length === 1 &&
      currentSelectedType === NodeType.Assets &&
      canViewPrivateAsset &&
      !isUpload,
  });

  const { publicAsset, isPending: isPublicAssetPending } = usePublicAsset(
    {
      shareId: query.shareId as string,
      assetId: selectedItems[0]?.id,
    },
    form,
    {
      enabled:
        !authIsLoading &&
        showInspector &&
        selectedItems.length === 1 &&
        isAsset(browserSelectedAssetsIds[0]?.type) &&
        currentSelectedType === NodeType.Assets &&
        !canViewPrivateAsset &&
        Boolean(query.shareId) &&
        Boolean(selectedItems[0]?.id),
    },
  );

  // useMultipleAsset resets the form straight from its query function; clear the baseline so a
  // return to a single, already-seen entity still reseeds it.
  const multiSelectForm = useMemo(
    () => ({
      reset: (formValues: any) => {
        baselineRef.current = null;
        form.reset(formValues);
      },
    }),
    [form],
  );

  // Replace the hardcoded values once the backend is ready
  const { multipleAssets, isMultipleAssetPending } = useMultipleAsset(
    multiSelectForm,
    isAllSelected && selectedNode?.type === DAMNodeType.Folder ? selectedNode.node.id : undefined,
    isAllSelected && selectedNode?.type === DAMNodeType.Album ? selectedNode.node.id : undefined,
    !isAllSelected ? selectedItems.map((asset) => asset.id) : undefined,
    includeSubFolderAssets,
    '',
    {
      enabled:
        showInspector &&
        multipleAssetSelected &&
        onlyAssetsSelected &&
        canViewPrivateAsset &&
        !isTrashPage &&
        !isUpload,
    },
  );

  // Guest counterpart of useMultipleAsset on a public share: aggregates the tag
  // intersection for a multi-selection (DM-1409 fix). Enabled only when the authed
  // path is not (no private-asset access on the share).
  const { publicMultipleAssets, isPublicMultipleAssetsFetching } = usePublicMultipleAssets(
    {
      shareId: query.shareId as string,
      assetIds: selectedItems.map((asset) => asset.id),
    },
    {
      enabled:
        showInspector &&
        multipleAssetSelected &&
        onlyAssetsSelected &&
        isPublicSharePage &&
        !canViewPrivateAsset &&
        !isAllSelected &&
        Boolean(query.shareId),
    },
  );

  // A refetch keeps the previous aggregation on screen, so only the very first selection has nothing to
  // show yet.
  const publicMultipleAssetsIsLoading = isPublicMultipleAssetsFetching && !publicMultipleAssets;

  const asset = useMemo(() => {
    return canViewPrivateAsset ? privateAsset : singleAssetShare ? data[0] : publicAsset;
  }, [canViewPrivateAsset, publicAsset, privateAsset, singleAssetShare, data]);

  const isAssetPending = useMemo(() => {
    return canViewPrivateAsset ? isPrivateAssetPending : isPublicAssetPending;
  }, [canViewPrivateAsset, isPublicAssetPending, isPrivateAssetPending]);

  const { album, isAlbumLoading, isAlbumFetching } = useAlbum(
    isTrashPage || isSharesPage ? selectedItems[0]?.id : describedNodeId,
    {
      refetchOnWindowFocus: false,
      enabled:
        showInspector &&
        currentSelectedType === NodeType.Albums &&
        canViewPrivateAlbum &&
        !isUpload &&
        describedNodeCouldBeAlbum &&
        !multipleAssetSelected &&
        (isTrashPage || isSharesPage ? Boolean(selectedItems[0]?.id) : Boolean(describedNodeId)),
    },
  );

  const { folder, isFolderLoading, isFolderFetching } = useFolder(
    isTrashPage || isSharesPage ? selectedItems[0]?.id : describedNodeId,
    {
      refetchOnWindowFocus: false,
      enabled:
        showInspector &&
        currentSelectedType === NodeType.Folders &&
        !isPublicSharePage &&
        !isUpload &&
        describedNodeCouldBeFolder &&
        !multipleAssetSelected &&
        (isTrashPage || isSharesPage ? Boolean(selectedItems[0]?.id) : Boolean(describedNodeId)),
    },
  );

  // Seeds name/description from the described entity, then keeps each field in step with it —
  // skipping a field only while it's the one currently being edited.
  const baselineRef = useRef<{ key: string; name: string; description: string } | null>(null);
  const editingFieldsRef = useRef<{ name: boolean; description: boolean }>({ name: false, description: false });

  useEffect(() => {
    // Pick the right object for Assets mode
    const assetTarget = singleAssetShare ? data[0] : privateAsset;

    // Pick the overall target by type
    const target =
      currentSelectedType === NodeType.Folders
        ? folder
        : currentSelectedType === NodeType.Albums
          ? album
          : currentSelectedType === NodeType.Assets
            ? assetTarget
            : undefined;

    if (!target) {
      return;
    }

    // The type belongs in the key: one panel describes assets, folders and albums.
    const entityKey = `${currentSelectedType}:${(target as any).id}`;
    const nextName = (target as any).name;
    const nextDescription = (target as any).description ?? '';

    if (entityKey !== baselineRef.current?.key) {
      baselineRef.current = { key: entityKey, name: nextName, description: nextDescription };
      form.reset({ name: nextName, description: nextDescription });
      return;
    }

    const baseline = baselineRef.current;

    if (!editingFieldsRef.current.name && nextName !== baseline.name) {
      baseline.name = nextName;
      form.resetField('name', { defaultValue: nextName });
    }

    if (!editingFieldsRef.current.description && nextDescription !== baseline.description) {
      baseline.description = nextDescription;
      form.resetField('description', { defaultValue: nextDescription });
    }
  }, [currentSelectedType, privateAsset, folder, album, singleAssetShare, data, form]);

  const isLoading = isAssetLoading || isAlbumLoading || isFolderLoading;
  const isFetching = isAssetFetching || isAlbumFetching || isFolderFetching;
  const isUploadMode = Boolean(isUpload);

  const assetProgress = assetsUploadProgress[asset?.id ?? temporaryAssets[0]?.id]?.progress;
  const isUploading =
    isUploadMode &&
    (uploadState === TaskState.InProgress || uploadState === TaskState.Cancelled) &&
    !(temporaryAssets[0]?.id in erroredAssets) &&
    assetProgress !== 100;
  const hasNoAssetVariants = asset ? Object.values(asset.variants ?? {}).every((value) => value === null) : false;
  const hasAssetVersions = (asset?.versions?.length ?? 0) > 0;
  const shareVersionsTabEnabled = isVersionsTabEnabledForShare({
    isPublicShare: isPublicSharePage,
    includeOriginalFile: shareConfig?.includeOriginalFile,
    versionCount: asset?.versions?.length ?? 0,
    canViewPrivateTabs,
    canDownloadAssets,
  });
  const downloadTabEnabled = isDownloadTabEnabled({
    isUploadMode,
    isManagePage,
    currentSelectedType: currentSelectedType as NodeType,
    multipleAssetSelected,
    canDownloadAssets,
  });
  const submissionsTabEnabled = isSubmissionsTabEnabled({
    assetSubmissionFeature,
    currentSelectedType: currentSelectedType as NodeType,
    canManageSubmissionLinks,
    canViewPrivateTabs,
    isManagePage,
  });
  const inspectorConfiguration = useMemo<Array<InspectorConfigurationItem>>(
    () => [
      {
        key: InspectorTabs.Upload,
        title: 'Upload',
        icon: Settings2,
        disabled: isUploading,
        show: canViewPrivateTabs && isUploadMode && uploadState === TaskState.NotStarted,
      },
      {
        key: InspectorTabs.Workflows,
        title: 'Workflow',
        icon: Workflow,
        disabled: !isManagePage && !selectedEntity && selectedEntityType !== EntityType.Workflow && isUploading,
        show: Boolean(isManagePage && selectedEntity && selectedEntityType === EntityType.Workflow && !isUploadMode),
      },
      {
        key: InspectorTabs.Tags,
        title: 'Tag',
        icon: TagsIcon,
        disabled: !isManagePage && !selectedEntity && selectedEntityType !== EntityType.Tag && isUploading,
        show: Boolean(isManagePage && selectedEntity && selectedEntityType === EntityType.Tag && !isUploadMode),
      },
      {
        key: InspectorTabs.ManageDestinations,
        title: 'Destination',
        icon: Route,
        disabled: !isManagePage && !selectedEntity && selectedEntityType !== EntityType.Destination && isUploading,
        show: Boolean(isManagePage && selectedEntity && selectedEntityType === EntityType.Destination),
      },
      {
        key: InspectorTabs.Variants,
        title: 'Variant',
        icon: Copy,
        disabled: !isManagePage && !selectedEntity && selectedEntityType !== EntityType.Variant && isUploading,
        show: Boolean(isManagePage && selectedEntity && selectedEntityType === EntityType.Variant && !isUploadMode),
      },
      {
        key: InspectorTabs.ManageMetadata,
        title: 'Metadata',
        icon: RectangleEllipsis,
        disabled: !isManagePage && !selectedEntity && selectedEntityType !== EntityType.Metadata && isUploading,
        show: Boolean(isManagePage && selectedEntity && selectedEntityType === EntityType.Metadata && !isUploadMode),
      },
      {
        key: InspectorTabs.Users,
        title: 'User',
        icon: UsersIcon,
        disabled: !isManagePage && !selectedEntity && selectedEntityType !== EntityType.User && isUploading,
        show: Boolean(isManagePage && selectedEntity && selectedEntityType === EntityType.User && !isUploadMode),
      },
      {
        key: InspectorTabs.Invitations,
        title: 'Invitation',
        icon: UsersIcon,
        disabled: !isManagePage && !selectedEntity && selectedEntityType !== EntityType.Invitation && isUploading,
        show: Boolean(isManagePage && selectedEntity && selectedEntityType === EntityType.Invitation && !isUploadMode),
      },
      {
        key: InspectorTabs.Information,
        title: 'Info',
        icon: Info,
        show: (!isManagePage || isUploadMode) && !isSubmitPage,
        disabled: isUploadMode && uploadSelectedAssetsIds.length === 0,
        ...(isUploadMode && { disabledMessage: 'Currently not available' }),
      },
      {
        key: InspectorTabs.Metadata,
        title: 'Metadata',
        icon: TextCursorInput,
        disabled:
          (!multipleAssetSelected && currentSelectedType !== NodeType.Assets) ||
          !onlyAssetsSelected ||
          isUploading ||
          (isUploadMode && uploadSelectedAssetsIds.length === 0),
        show:
          !isManagePage &&
          (isUploadMode ||
            (!isUploadMode &&
              currentSelectedType !== NodeType.Albums &&
              currentSelectedType !== NodeType.Folders &&
              (currentSelectedType === NodeType.Assets || onlyAssetsSelected))),
        ...(isUploadMode && { disabledMessage: 'Currently not available' }),
      },
      {
        key: InspectorTabs.Notes,
        title: 'Notes',
        icon: NotebookPen,
        disabled: multipleAssetSelected || currentSelectedType !== NodeType.Assets || isUploading,
        show: !(
          isUploadMode ||
          multipleAssetSelected ||
          isManagePage ||
          !canViewPrivateTabs ||
          currentSelectedType !== NodeType.Assets
        ),
      },
      {
        key: InspectorTabs.Comments,
        title: 'Comments',
        icon: MessagesSquare,
        disabled: multipleAssetSelected || currentSelectedType !== NodeType.Assets || isUploading,
        show:
          (!isPublicSharePage || (isPublicSharePage && shareCollaborationFeature)) &&
          canViewComments &&
          !(isUploadMode || multipleAssetSelected || isManagePage || currentSelectedType !== NodeType.Assets),
      },
      {
        key: InspectorTabs.Versions,
        title: 'Versions',
        icon: Images,
        disabled:
          currentSelectedType !== NodeType.Assets ||
          !asset ||
          !shareVersionsTabEnabled ||
          !hasAssetVersions ||
          multipleAssetSelected ||
          isUploading,
        show: !(isUploadMode || isManagePage || currentSelectedType !== NodeType.Assets || multipleAssetSelected),
        requiresDownloadCapability: true,
      },
      {
        key: InspectorTabs.Download,
        title: 'Download',
        icon: DownloadIcon,
        disabled: multipleAssetSelected || hasNoAssetVariants || currentSelectedType !== NodeType.Assets || isUploading,
        disabledMessage: multipleAssetSelected
          ? tInspector('multipleAssetsSelected')
          : tInspector('noInformationAvailable'),
        show: downloadTabEnabled,
        requiresDownloadCapability: true,
      },
      {
        key: InspectorTabs.Destinations,
        title: 'Destinations',
        icon: Route,
        disabled: multipleAssetSelected || isUploading,
        show:
          !isUploadMode &&
          currentSelectedType !== NodeType.Folders &&
          canViewPrivateTabs &&
          destinationsFeature &&
          !isManagePage,
      },
      {
        key: InspectorTabs.Sharing,
        title: 'Sharing',
        icon: Share,
        disabled:
          (isSharesPage
            ? false
            : !(
                (currentSelectedType === NodeType.Albums && album) ||
                (currentSelectedType === NodeType.Assets && asset)
              )) || isUploading,
        show:
          (isSharesPage && (!onlyAssetsSelected || !multipleAssetSelected)) ||
          !(
            isManagePage ||
            !canViewPrivateTabs ||
            isUploadMode ||
            multipleAssetSelected ||
            currentSelectedType === NodeType.Folders
          ),
      },
      {
        key: InspectorTabs.Submissions,
        title: tSubmissions('tabTitle'),
        icon: UserPen,
        disabled: multipleAssetSelected || isUploading,
        show: submissionsTabEnabled,
      },
      {
        key: InspectorTabs.AuditLog,
        title: 'Audit',
        icon: Logs,
        disabled: multipleAssetSelected || isUploading,
        show:
          !isUploadMode &&
          canViewPrivateTabs &&
          !multipleAssetSelected &&
          (!isManagePage || (selectedEntity !== null && selectedEntityType !== EntityType.Invitation)),
      },
    ],
    [
      isUploading,
      tInspector,
      canViewPrivateTabs,
      canViewComments,
      isUploadMode,
      uploadState,
      isManagePage,
      selectedEntity,
      selectedEntityType,
      uploadSelectedAssetsIds.length,
      multipleAssetSelected,
      currentSelectedType,
      onlyAssetsSelected,
      asset,
      hasAssetVersions,
      shareVersionsTabEnabled,
      downloadTabEnabled,
      destinationsFeature,
      isSharesPage,
      isPublicSharePage,
      album,
      hasNoAssetVariants,
      shareCollaborationFeature,
      submissionsTabEnabled,
      tSubmissions,
      isSubmitPage,
    ],
  );

  const capabilityFilteredInspectorConfiguration = useMemo(
    () => getCapabilityFilteredInspectorConfiguration(inspectorConfiguration, canDownloadAssets),
    [inspectorConfiguration, canDownloadAssets],
  );

  // When `visibleTabs` changes, make sure we're still on a valid tab
  const visibleTabs = useMemo(
    () => capabilityFilteredInspectorConfiguration.filter(({ show, disabled }) => show && !disabled).map((t) => t.key),
    [capabilityFilteredInspectorConfiguration],
  );

  const firstMount = useRef(true);
  // A tab requested through the URL (notification CTAs) can be unavailable on the first
  // render: on a share page `canViewComments` stays false until `shareConfig` resolves.
  // Keep the request pending until the tab actually exists instead of dropping it.
  const appliedQueryTab = useRef<InspectorTabs | null>(null);

  useLayoutEffect(() => {
    let ideal = isUpload ? InspectorTabs.Upload : defaultTab;

    if (isManagePage && selectedEntityType) {
      switch (selectedEntityType) {
        case EntityType.Workflow:
          ideal = InspectorTabs.Workflows;
          break;
        case EntityType.Tag:
          ideal = InspectorTabs.Tags;
          break;
        case EntityType.Destination:
          ideal = InspectorTabs.ManageDestinations;
          break;
        case EntityType.Variant:
          ideal = InspectorTabs.Variants;
          break;
        case EntityType.Metadata:
          ideal = InspectorTabs.ManageMetadata;
          break;
        case EntityType.User:
          ideal = InspectorTabs.Users;
          break;
        case EntityType.Invitation:
          ideal = InspectorTabs.Invitations;
          break;
        default:
          ideal = defaultTab;
          break;
      }
    }

    if (isSharesPage) {
      ideal = InspectorTabs.Sharing;
    }

    if (requestedInspectorTabFromQuery && visibleTabs.includes(requestedInspectorTabFromQuery)) {
      ideal = requestedInspectorTabFromQuery;
    }

    const hasPendingQueryTab =
      requestedInspectorTabFromQuery !== null &&
      appliedQueryTab.current !== requestedInspectorTabFromQuery &&
      visibleTabs.includes(requestedInspectorTabFromQuery);

    if (firstMount.current || hasPendingQueryTab || !visibleTabs.includes(currentInspectorTab)) {
      const toSet = visibleTabs.includes(ideal) ? ideal : (visibleTabs[0] ?? defaultTab);
      setCurrentInspectorTab(toSet);
      firstMount.current = false;

      if (hasPendingQueryTab) {
        appliedQueryTab.current = requestedInspectorTabFromQuery;
      }
    }
  }, [
    isManagePage,
    requestedInspectorTabFromQuery,
    selectedEntityType,
    visibleTabs,
    selectedItems?.length,
    isSharesPage,
    currentInspectorTab,
    defaultTab,
    isUpload,
  ]);

  // Keyed off the viewed/selected asset, folder or album's own trashed state, not the page — a
  // trashed node is read-only wherever it's opened (browse, search results, single asset preview).
  const viewedAssetTrashed = Boolean(asset?.trashedAt || asset?.trashed || folder?.trashedAt || album?.trashedAt);
  const trashReadOnly = (trashedSelectedAssets || viewedAssetTrashed) && !isUpload;
  // The Metadata panel is read-only for viewers without private-tab access and in the bin; the save
  // bar and the fields have to agree on it.
  const metadataReadOnly = !canViewPrivateTabs || trashReadOnly;
  // The Metadata panel's scrolling element. The save bar sits outside it, so the pane shrinks to make
  // room rather than the bar covering the fields — and the bar needs the ref to scroll the edited row
  // back into view when its own appearance pushes it below the edge.
  const metadataScrollRef = useRef<HTMLDivElement>(null);

  const activeVersion = useMemo(() => {
    if (asset && Array.isArray(asset.versions)) {
      const v = asset.versions.find((version: BaseAssetVersion) => version.active);
      return v ?? asset.versions[0];
    }
    return null;
  }, [asset]);

  const selectionType = isUpload
    ? DAMNodeType.Asset
    : currentSelectedType === NodeType.Folders
      ? 'folder'
      : currentSelectedType === NodeType.Albums
        ? 'album'
        : selectedItems.every((item) => isAsset(item.type))
          ? DAMNodeType.Asset
          : selectedItems.every((item) => item.type === DAMNodeType.Album)
            ? DAMNodeType.Album
            : selectedItems.every((item) => item.type === DAMNodeType.Folder)
              ? DAMNodeType.Folder
              : 'item';

  // A tree folder/album selection leaves selectedItems empty but still has exactly one
  // thing selected, which is why the old concatenation rendered a bare "folder selected".
  const selectedHeaderCount = isAllSelected ? (pagination?.count ?? 0) : selectedItems.length || 1;

  const renderContent = () => {
    if (isHomePage && !selectedNode && !asset && !search && !isPublicSharePage && !isUpload) {
      return <ServiceDeskSection />;
    }

    if (
      multipleAssetSelected &&
      onlyAssetsSelected &&
      isMultipleAssetPending &&
      !isPublicSharePage &&
      !isTrashPage &&
      !isUpload
    ) {
      return (
        <div className="flex h-screen items-center justify-center">
          <Loader2 className="animate-spin stroke-neutral-950 dark:stroke-white" />
        </div>
      );
    }

    if (multipleAssetSelected && !onlyAssetsSelected) {
      return (
        <div className="flex h-full flex-col items-center justify-center p-4">
          <h2 className="text-center text-lg font-semibold tracking-tight text-balance text-neutral-500 dark:text-neutral-400">
            {tInspector('multipleItemsSelected')}
          </h2>
          <p className="text-center text-sm font-semibold tracking-tight text-balance text-neutral-400 dark:text-neutral-500">
            {tInspector('selectSingleItem')}
          </p>
        </div>
      );
    }

    const nodeMap = {
      [NodeType.Assets]: asset,
      [NodeType.Folders]: folder,
      [NodeType.Albums]: album,
    } as const;

    const isTrashed = Boolean(nodeMap[currentSelectedType as keyof typeof nodeMap]?.trashedAt);

    return (
      <div className="flex h-full flex-col space-y-px">
        {selectedItems.length > 0 ||
        selectedNode ||
        singleAssetShare ||
        (isManagePage && selectedEntity && selectedEntityType) ||
        isUpload ? (
          <>
            <div
              className={cn(
                'flex max-h-12 min-h-12 items-center justify-between gap-3 border-t border-toolbar-bevel bg-ui-base px-4 text-lg font-semibold tracking-tight',
              )}
            >
              {isUpload ? (
                <p className="line-clamp-1 text-sm wrap-anywhere">{tLabels('upload.settings')}</p>
              ) : (
                <>
                  <p className="line-clamp-1 text-sm wrap-anywhere">
                    {isManagePage && selectedEntity && selectedEntityType
                      ? 'name' in selectedEntity.original && selectedEntity?.original?.name
                      : tCommon('properties')}
                  </p>
                  <p
                    className={cn(
                      'text-balance text-left text-[13px] text-text-gray-alternate transition-all duration-200',
                    )}
                  >
                    {isManagePage && selectedEntity && selectedEntityType
                      ? selectedEntityType
                      : tSelection('selectedCount', { count: selectedHeaderCount, type: selectionType })}
                  </p>
                </>
              )}
            </div>
            {!multipleAssetSelected && isTrashed && !isLoading && (
              <TrashedBanner
                currentSelectedType={currentSelectedType as NodeType}
                disabled={multipleAssetSelected && !isTrashed && isLoading}
              />
            )}
            {/* The panel's form outlives any one section: the save bar below saves everything the
                Metadata panel holds, so the form cannot live inside the metadata accordion. Keyed on
                the selection so switching assets starts a fresh form rather than resetting one. */}
            <Tabs
              orientation="vertical"
              className="flex size-full space-x-px overflow-y-auto"
              variant="tabs"
              value={currentInspectorTab}
              onValueChange={(tab) => setCurrentInspectorTab(tab as InspectorTabs)}
            >
              {capabilityFilteredInspectorConfiguration
                .filter(({ show }) => show)
                .map((tab) => (
                  <TabsContent
                    key={tab.key}
                    value={tab.key}
                    // The Metadata panel is a column: its fields scroll in an inner element so the
                    // save bar can sit at the panel's foot without scrolling away with them — and
                    // without being sticky, which is what used to cost the accordion its clipping
                    // (and so its open/close animation). Every other panel scrolls as itself.
                    // `overflow-hidden` here is load-bearing, not decorative: without it this pane
                    // doesn't clip its own content, so anything that outgrows the inner scroll
                    // area (DM-2606) leaks past it, and the outer `Tabs` root's own overflow-y-auto
                    // — there only to stop that flex item from growing unbounded — becomes the
                    // visible scrollbar instead, dragging the whole tab bar (icons included) along
                    // with it and pushing the save bar out of view.
                    className={cn(
                      'relative size-full bg-ui-panel! focus-visible:outline-hidden',
                      tab.key === InspectorTabs.Metadata ? 'flex min-h-0 flex-col overflow-hidden' : 'overflow-y-auto',
                    )}
                  >
                    {tab.key === InspectorTabs.Upload ? (
                      <>{isUpload && (uploadTabContent ?? <UploadsAccordion />)}</>
                    ) : tab.key === InspectorTabs.Workflows ? (
                      <>
                        {Boolean(selectedEntity?.original.id) && selectedEntityType === EntityType.Workflow && (
                          <WorkflowFields
                            // Remount per workflow: the form preserves dirty values across
                            // refetches (keepDirtyValues), which must not survive switching
                            // to another workflow (DM-1113).
                            key={selectedEntity?.original.id as string}
                            workflowId={selectedEntity?.original.id as string}
                          />
                        )}
                      </>
                    ) : tab.key === InspectorTabs.Tags ? (
                      <>
                        {Boolean(selectedEntity?.original.id) && selectedEntityType === EntityType.Tag && (
                          <TagFields tagId={selectedEntity?.original.id as string} />
                        )}
                      </>
                    ) : tab.key === InspectorTabs.Variants ? (
                      <>
                        {Boolean(selectedEntity?.original.id) && selectedEntityType === EntityType.Variant && (
                          <VariantFields
                            variantId={selectedEntity?.original.id as string}
                            enabled={Boolean(selectedEntity?.original.id) && selectedEntityType === EntityType.Variant}
                          />
                        )}
                      </>
                    ) : tab.key === InspectorTabs.Users ? (
                      <>
                        {Boolean(selectedEntity?.original.id) && selectedEntityType === EntityType.User && (
                          <UserFields
                            user={selectedEntity as RowEntity}
                            enabled={Boolean(selectedEntity?.original.id)}
                          />
                        )}
                      </>
                    ) : tab.key === InspectorTabs.Invitations ? (
                      <>
                        {Boolean(selectedEntity?.original.id) && selectedEntityType === EntityType.Invitation && (
                          <InvitationFields invitation={selectedEntity as RowEntity} />
                        )}
                      </>
                    ) : tab.key === InspectorTabs.ManageMetadata ? (
                      <>
                        {Boolean(selectedEntity?.original.id) && selectedEntityType === EntityType.Metadata && (
                          <MetadataFields metadataId={selectedEntity?.original.id as string} />
                        )}
                      </>
                    ) : tab.key === InspectorTabs.ManageDestinations ? (
                      <>
                        {Boolean(selectedEntity?.original.id) && selectedEntityType === EntityType.Destination && (
                          <DestinationFields destinationId={selectedEntity?.original.id as string} />
                        )}
                      </>
                    ) : tab.key === InspectorTabs.Information ? (
                      <>
                        {Boolean(currentSelectedType) && !isUpload && (
                          <PreviewAccordion
                            asset={asset}
                            currentSelectedType={currentSelectedType as NodeType}
                            isUpload={isUpload ?? false}
                            temporaryAssets={temporaryAssets}
                            selectedAssetIdsLength={selectedItems?.length}
                            assetProgress={assetProgress}
                            erroredAssets={erroredAssets}
                            disabled={multipleAssetSelected}
                            activeVersion={activeVersion}
                            isAssetPending={isAssetPending}
                            singleAssetView={singleAssetView}
                          />
                        )}

                        {!isUploading && (
                          <>
                            <InformationAccordion
                              disabled={multipleAssetSelected}
                              asset={asset}
                              folder={folder}
                              album={album}
                              form={form}
                              currentSelectedType={currentSelectedType as NodeType}
                              selectedItems={selectedItems}
                              isLoading={isLoading}
                              isFetching={isFetching}
                              readOnly={!canViewPrivateTabs || trashReadOnly}
                              selectedAssetIdsLength={selectedItems?.length}
                              temporaryAssets={temporaryAssets}
                              activeVersion={activeVersion}
                              selectedNode={nodeMap[currentSelectedType as keyof typeof nodeMap]}
                              isUpload={isUpload}
                              editingFieldsRef={editingFieldsRef}
                            >
                              <CameraInformation asset={asset} disabled={multipleAssetSelected} />

                              {(currentSelectedType === NodeType.Assets ||
                                (multipleAssetSelected && onlyAssetsSelected)) && (
                                <div className="p-3">
                                  <Label variant={LabelVariants.INSPECTOR_SECTION}>{tCommon('rating')}</Label>
                                  <Rating
                                    readOnly={
                                      trashReadOnly || !canViewPrivateTabs || multipleAssetSelected || isAllSelected
                                    }
                                    size={30}
                                    rating={
                                      multipleAssetSelected
                                        ? 0
                                        : isUpload
                                          ? (manualMetadata[uploadSelectedAssetsIds[0]?.id]?.rating ??
                                            (uploadSelectedAssetsIds[0]
                                              ? csvData[getUploadCsvKey(uploadSelectedAssetsIds[0])]?.rating
                                              : undefined) ??
                                            0)
                                          : asset?.rating
                                    }
                                    isLoading={isLoading}
                                    onChange={(newRating) => {
                                      if (isUpload) {
                                        setManualMetadata(
                                          uploadSessionId,
                                          {
                                            rating: newRating,
                                          },
                                          [
                                            {
                                              id: uploadSelectedAssetsIds[0].id,
                                              name: uploadSelectedAssetsIds[0].name,
                                              originalName: uploadSelectedAssetsIds[0].originalName,
                                            },
                                          ],
                                          true,
                                        );
                                        return;
                                      }

                                      if (search && asset?.id) {
                                        setRatingOverride(asset.id, newRating);
                                      }
                                      setRating(asset.id, asset.rating, newRating);
                                    }}
                                  />
                                </div>
                              )}
                            </InformationAccordion>

                            {multipleAssetSelected &&
                              onlyAssetsSelected &&
                              !canViewPrivateAsset &&
                              (publicMultipleAssets?.name || publicMultipleAssets?.description) && (
                                <div className="flex flex-col space-y-3 p-3">
                                  {publicMultipleAssets.name && (
                                    <div
                                      className="line-clamp-2 text-sm font-semibold wrap-anywhere"
                                      title={publicMultipleAssets.name}
                                    >
                                      {publicMultipleAssets.name}
                                    </div>
                                  )}
                                  {publicMultipleAssets.description && (
                                    <div
                                      className="line-clamp-4 text-[13px] wrap-anywhere"
                                      title={publicMultipleAssets.description}
                                    >
                                      {publicMultipleAssets.description}
                                    </div>
                                  )}
                                </div>
                              )}

                            {(currentSelectedType === NodeType.Assets ||
                              (multipleAssetSelected && onlyAssetsSelected)) && (
                              <TagsAccordion
                                // In multi-select, drive tags from the aggregation only — passing the
                                // single asset would let the accordion flash that asset's full tag list
                                // while the intersection refetches.
                                asset={multipleAssetSelected ? undefined : asset}
                                currentInspectorTab={currentInspectorTab}
                                multipleAssetsTags={
                                  multipleAssetSelected
                                    ? (canViewPrivateAsset ? multipleAssets : publicMultipleAssets)?.tags
                                    : undefined
                                }
                                selectedItems={isAllSelected ? undefined : selectedItems}
                                folderId={
                                  isAllSelected
                                    ? selectedNode?.type === DAMNodeType.Folder
                                      ? selectedNode.node.id
                                      : undefined
                                    : undefined
                                }
                                albumId={
                                  isAllSelected
                                    ? selectedNode?.type === DAMNodeType.Album
                                      ? selectedNode.node.id
                                      : undefined
                                    : undefined
                                }
                                disabled={!canViewPrivateTabs && multipleAssetSelected}
                                readOnly={!canViewPrivateTabs || trashReadOnly}
                                isUpload={isUpload}
                              />
                            )}

                            {(currentSelectedType === NodeType.Assets ||
                              (multipleAssetSelected && onlyAssetsSelected)) &&
                              canViewPrivateTabs && (
                                <WorkflowAccordion
                                  asset={asset}
                                  multipleAssetsWorkflows={
                                    multipleAssetSelected ? multipleAssets?.workflows : undefined
                                  }
                                  selectedItems={isAllSelected ? undefined : selectedItems}
                                  folderId={
                                    isAllSelected
                                      ? selectedNode?.type === DAMNodeType.Folder
                                        ? selectedNode.node.id
                                        : undefined
                                      : undefined
                                  }
                                  albumId={
                                    isAllSelected
                                      ? selectedNode?.type === DAMNodeType.Album
                                        ? selectedNode.node.id
                                        : undefined
                                      : undefined
                                  }
                                  disabled={!canViewPrivateTabs && multipleAssetSelected}
                                  readOnly={trashReadOnly}
                                  enabled={currentInspectorTab === InspectorTabs.Information && isAuthenticated}
                                  isUpload={isUpload}
                                />
                              )}

                            {currentSelectedType === NodeType.Assets &&
                              canViewPrivateTabs &&
                              !multipleAssetSelected && (
                                <NodeLocations ancestors={asset?.ancestors} albumPaths={asset?.albumPaths} />
                              )}

                            {(currentSelectedType === NodeType.Assets ||
                              (multipleAssetSelected && onlyAssetsSelected)) &&
                              canViewPrivateTabs &&
                              !isUpload && (
                                <Accordion type="single" collapsible>
                                  <FileInformationAccordion
                                    disabled={multipleAssetSelected}
                                    disabledMessage={
                                      multipleAssetSelected
                                        ? tInspector('multipleAssetsSelected')
                                        : tInspector('noInformationAvailable')
                                    }
                                    asset={asset}
                                  />
                                </Accordion>
                              )}
                          </>
                        )}
                      </>
                    ) : tab.key === InspectorTabs.Metadata ? (
                      <MetadataPanelFormProvider
                        // In multi-select, drive metadata from the aggregation only — passing the single
                        // asset would let the accordion flash that asset's own metadata while it refetches.
                        asset={multipleAssetSelected ? undefined : asset}
                        multipleAssetsMetadata={
                          multipleAssetSelected
                            ? (canViewPrivateAsset ? multipleAssets : publicMultipleAssets)?.metadata
                            : undefined
                        }
                        multipleAssetsMetadataIsLoading={publicMultipleAssetsIsLoading}
                        selectedItems={isUpload ? uploadSelectedAssetsIds : isAllSelected ? undefined : selectedItems}
                        folderId={
                          isAllSelected
                            ? selectedNode?.type === DAMNodeType.Folder
                              ? selectedNode.node.id
                              : undefined
                            : undefined
                        }
                        albumId={
                          isAllSelected
                            ? selectedNode?.type === DAMNodeType.Album
                              ? selectedNode.node.id
                              : undefined
                            : undefined
                        }
                        isUpload={isUpload}
                        contributorMetadataDefinitions={contributorMetadataDefinitions}
                        enabled={
                          !guest &&
                          isAuthenticated &&
                          onlyAssetsSelected &&
                          currentInspectorTab === InspectorTabs.Metadata &&
                          currentAccordionItems.includes('metadata') &&
                          (isUpload ?? (multipleAssetSelected ? true : Boolean(asset?.id)))
                        }
                      >
                        {/* `pb-2` keeps the last section off the bar once scrolled to the very end.
                              The `data-cy` is for the dam-qa suite: this, not the tab panel, is the
                              element that scrolls in the Metadata tab, and it has to be reachable
                              without keying off a utility class. */}
                        <div
                          ref={metadataScrollRef}
                          data-cy="metadata-scroll-area"
                          className="min-h-0 flex-1 overflow-y-auto pb-2"
                        >
                          <Accordion
                            type="multiple"
                            className="w-full min-w-0"
                            onValueChange={(accordion) => setCurrentAccordionItems(accordion)}
                            value={currentAccordionItems}
                          >
                            {onlyAssetsSelected && (
                              <MetadataAccordion
                                multipleAssetsMetadata={
                                  multipleAssetSelected
                                    ? (canViewPrivateAsset ? multipleAssets : publicMultipleAssets)?.metadata
                                    : undefined
                                }
                                selectedItems={
                                  isUpload ? uploadSelectedAssetsIds : isAllSelected ? undefined : selectedItems
                                }
                                disabled={!canViewPrivateTabs && multipleAssetSelected}
                                readOnly={metadataReadOnly}
                                isUpload={isUpload}
                              />
                            )}
                          </Accordion>
                        </div>
                        {!metadataReadOnly && <MetadataSaveBar scrollContainerRef={metadataScrollRef} />}
                      </MetadataPanelFormProvider>
                    ) : tab.key === InspectorTabs.Notes ? (
                      <NotesAccordion
                        asset={asset}
                        disabled={multipleAssetSelected}
                        readOnly={trashReadOnly}
                        enabled={currentInspectorTab === InspectorTabs.Notes && isAuthenticated && Boolean(asset?.id)}
                      />
                    ) : tab.key === InspectorTabs.Comments ? (
                      <CommentsPanel
                        asset={asset}
                        enabled={
                          currentInspectorTab === InspectorTabs.Comments && isAuthenticated && Boolean(asset?.id)
                        }
                      />
                    ) : tab.key === InspectorTabs.Versions ? (
                      <VersionsAccordion
                        disabled={multipleAssetSelected}
                        asset={asset}
                        readOnly={trashReadOnly || isPublicSharePage}
                      />
                    ) : tab.key === InspectorTabs.Download ? (
                      <VariantsAccordion disabled={multipleAssetSelected || hasNoAssetVariants} asset={asset} />
                    ) : tab.key === InspectorTabs.Sharing ? (
                      <SharesAccordion
                        asset={currentSelectedType === NodeType.Assets ? asset : data[0]}
                        item={currentSelectedType === NodeType.Assets ? asset : album}
                        currentSelectedType={
                          (currentSelectedType === NodeType.Assets
                            ? 'asset'
                            : currentSelectedType === NodeType.Albums
                              ? 'album'
                              : 'folder') as NodeType
                        }
                        disabled={multipleAssetSelected}
                        // item={
                        //   currentSelectedType === NodeType.Assets ? asset : currentSelectedType === NodeType.Albums ? album : folder
                        // }
                        enabled={
                          currentInspectorTab === InspectorTabs.Sharing &&
                          isAuthenticated &&
                          Boolean(nodeMap[currentSelectedType as keyof typeof nodeMap]?.nodeId)
                        }
                        readOnly={trashReadOnly}
                      />
                    ) : tab.key === InspectorTabs.Submissions ? (
                      <SubmissionsAccordion
                        nodeId={folder?.nodeId ?? ''}
                        folderName={folder?.name ?? ''}
                        readOnly={trashReadOnly}
                        enabled={
                          currentInspectorTab === InspectorTabs.Submissions &&
                          isAuthenticated &&
                          Boolean(folder?.nodeId)
                        }
                      />
                    ) : tab.key === InspectorTabs.Destinations ? (
                      <DestinationAccordion
                        disabled={multipleAssetSelected}
                        item={currentSelectedType === NodeType.Assets ? asset : album}
                        currentSelectedType={currentSelectedType as NodeType}
                        currentInspectorTab={currentInspectorTab}
                        readOnly={trashReadOnly}
                        enabled={currentInspectorTab === InspectorTabs.Destinations && isAuthenticated}
                      />
                    ) : (
                      tab.key === InspectorTabs.AuditLog && (
                        <>
                          {(!multipleAssetSelected || (isManagePage && !selectedEntity && !selectedEntityType)) && (
                            <AuditLogAccordion
                              selectedItems={
                                isManagePage && selectedEntity ? selectedEntity?.original.id : selectedItems
                              }
                              enabled={currentInspectorTab === InspectorTabs.AuditLog && isAuthenticated}
                              {...(isManagePage &&
                                selectedEntityType && {
                                  entity: selectedEntityType,
                                })}
                            />
                          )}
                        </>
                      )
                    )}
                  </TabsContent>
                ))}

              <InspectorTabList
                configuration={capabilityFilteredInspectorConfiguration}
                authIsLoading={authIsLoading}
                profileLoading={profileLoading}
              />
            </Tabs>
          </>
        ) : (
          <h2 className="flex h-full items-center justify-center bg-ui-container p-4 text-center text-lg font-semibold tracking-tight text-balance text-neutral-400">
            {tEmptyStates('nothingSelected.title')}
          </h2>
        )}
      </div>
    );
  };

  return <div className="@container/inspector relative flex h-full flex-col">{renderContent()}</div>;
};
